/**
 * Eine einklappbare Zeile über die volle Breite einer Karte — die Zeilen
 * „So wird gerechnet", „Verlauf" und „Verteilung des PV-Stroms" aus
 * `ui_kits/dashboard/MesskonzeptScreen.jsx`.
 *
 * **Eingeklappt steht die Zusammenfassung in der Zeile.** Was einmal
 * eingerichtet und danach selten angefasst wird, soll nicht die Seite füllen —
 * aber auch nicht verschwinden: Die erste Formel, die geltende Regel, die Zahl
 * der Fassungen sind ohne Klick zu lesen.
 *
 * Ein echter Knopf mit `aria-expanded` und `aria-controls`; der Inhalt hängt
 * darunter und wird vom Aufrufer nur gezeichnet, wenn `open` gilt.
 */
import type { ReactNode } from "react";
import { icons } from "@hausmeter/ui";

export function Disclosure({
  id,
  title,
  summary,
  mono = false,
  open,
  onToggle,
}: {
  /** Die Kennung des Inhalts darunter — für `aria-controls`. */
  id: string;
  title: ReactNode;
  /** Was eingeklappt neben dem Titel steht. */
  summary?: ReactNode;
  /** Die Zusammenfassung ist eine Formel oder Zahl und steht in Monoschrift. */
  mono?: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={id}
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        padding: "12px 18px",
        border: "none",
        borderTop: "1px solid var(--border-subtle)",
        background: "transparent",
        cursor: "pointer",
        fontFamily: "var(--font-body)",
        fontSize: 12.5,
        fontWeight: 600,
        color: "var(--text-muted)",
        textAlign: "left",
      }}
    >
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          flexShrink: 0,
          transform: open ? "rotate(90deg)" : "none",
          transition: "transform 120ms ease",
        }}
      >
        <icons.IconChevronRight size={14} />
      </span>
      <span style={{ flexShrink: 0 }}>{title}</span>
      {!open && summary !== undefined && (
        <span
          style={{
            minWidth: 0,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 400,
            color: "var(--text-muted)",
            ...(mono ? { fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" } : {}),
          }}
        >
          {summary}
        </span>
      )}
    </button>
  );
}
